import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageShell";
import { CTASection } from "@/components/site/CTASection";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical } from "@/components/site/site-config";
import { SERVICES } from "@/components/site/constants";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Roofing Services in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Explore roof replacement, repair, metal, shingle and flat roofing, gutters, soffit, inspections, storm damage and wind-mitigation services in Citrus County, FL." },
      { property: "og:title", content: "Roofing Services in Citrus County, FL | Destination Roofing" },
      { property: "og:description", content: "Every roofing service Destination Roofing offers throughout Citrus County, FL." },
      { property: "og:url", content: canonical("/services") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/services") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(breadcrumbListJsonLd([{ label: "Services", href: "/services" }])),
    }],
  }),
  component: Page,
});

const DESCRIPTIONS: Record<string, string> = {
  "Roof Replacement": "Full tear-off, deck inspection, new underlayment and a new shingle, metal or flat roof system installed to Florida wind standards.",
  "Roof Repair": "Roof leak repair, missing shingles, wind and storm damage, cracked flashings and pipe boots — repaired at the actual source.",
  "Roof Inspection": "Honest, no-pressure inspection with a written estimate and clear repair-versus-replacement guidance.",
  "Storm Damage Roof Repair": "Post-storm inspection, temporary dry-in, documentation and repair for wind, hail and debris damage.",
  "Metal Roofing": "Standing-seam and exposed-fastener metal roof installation and repair for long-term Florida performance.",
  "Shingle Roofing": "Architectural asphalt shingle installation with Atlas products and wind-rated installation.",
  "Flat Roofing": "TPO and modified-bitumen low-slope systems for lanais, Florida rooms, additions and commercial buildings.",
  "Mobile Home Roofing": "Metal and TPO roof-overs, roof coatings and leak repairs for mobile and manufactured homes.",
  "Gutters": "Seamless aluminum gutter installation and repair to protect fascia, soffit and foundation.",
  "Soffit": "Soffit and fascia repair or replacement — critical for attic ventilation and roof-edge protection.",
  "Third Nail Wind Mitigation": "Strengthening eligible roof-to-wall connections. Ask whether it may help you qualify for insurance credits.",
  "Commercial Roofing": "Commercial roof inspection, repair, replacement and low-slope systems planned around your operations.",
};

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }]} />
      <PageHero
        eyebrow="Our Services"
        title="Roofing Services in Citrus County, FL"
        subtitle="Everything Destination Roofing offers, from a small leak repair to a complete roof replacement. Family-owned, licensed and insured."
      />
      <section className="mx-auto max-w-7xl px-4 py-14 md:py-20">
        <div className="grid gap-5 md:grid-cols-2">
          {SERVICES.map(s => (
            <Link key={s.slug} to={s.slug} className="group rounded-xl border border-border bg-white p-6 hover:border-brand hover:-translate-y-0.5 transition-all shadow-[var(--shadow-card)]">
              <h2 className="font-display font-bold text-xl group-hover:text-brand">
                <span className="inline-flex items-center gap-2">{s.title} <ArrowRight className="size-4 opacity-60 group-hover:translate-x-1 transition-transform" /></span>
              </h2>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                {DESCRIPTIONS[s.title] ?? s.short}
              </p>
            </Link>
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}
